import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTutorialModal } from '@/contexts/TutorialModalContext';
import { useTutorial, useTutorials } from '@/hooks/useTutorials';
import { useCategoriesHierarchical } from '@/hooks/useCategories';
import TutorialViewer from '@/components/TutorialViewer/TutorialViewer';
import TutorialSidebar from './TutorialSidebar';

const TutorialModal = () => {
  const {
    isOpen,
    tutorialSlug,
    viewMode,
    stepId,
    closeModal,
    openModal
  } = useTutorialModal();
  
  const { data: tutorialData } = useTutorial(tutorialSlug ?? undefined);
  const { data: tutorialsData, isLoading: tutorialsLoading } = useTutorials();
  const { data: categoriesData, isLoading: categoriesLoading } = useCategoriesHierarchical();
  
  const navigate = useNavigate();

  // Escutar evento de navegação quando o modal é fechado via hash
  useEffect(() => {
    const handleNavigate = (event: CustomEvent) => {
      if (event.detail && event.detail.path) {
        navigate(event.detail.path, { replace: true });
      }
    };
    window.addEventListener('tutorial-modal-close-navigate', handleNavigate as EventListener);
    return () => window.removeEventListener('tutorial-modal-close-navigate', handleNavigate as EventListener);
  }, [navigate]);

  // Fechar modal com ESC
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) closeModal();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, closeModal]);

  if (!isOpen || !tutorialSlug) return null;

  const currentTutorial = (tutorialData as any)?.data;
  const categories = (categoriesData as any) || [];
  const allTutorials = (tutorialsData as any)?.data || [];

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[10000] bg-black/60 backdrop-blur-md flex items-center justify-center p-0 lg:p-6 overflow-hidden">
      
      {/* Modal Container: Learnfy Style */}
      <motion.div 
        initial={{ opacity: 0, y: 30, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 30, scale: 0.98 }}
        className="w-full h-full max-w-[1600px] lg:h-[90vh] bg-white lg:rounded-[2.5rem] shadow-[0_50px_100px_-20px_rgba(0,0,0,0.5)] overflow-hidden flex flex-col lg:flex-row relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Mobile Close Button (Overlays everything) */}
        <button 
          onClick={() => closeModal()}
          className="lg:hidden absolute top-4 right-4 z-50 w-10 h-10 bg-black/50 backdrop-blur-md text-white rounded-full flex items-center justify-center"
        >
          <X size={20} />
        </button>

        {/* Sidebar: Fixed Modules List */}
        <div className="hidden lg:block">
           <TutorialSidebar 
             categories={categories}
             allTutorials={allTutorials}
             currentTutorialId={currentTutorial?.id || currentTutorial?.Id}
             onTutorialClick={(t) => openModal(t.slug || t.Slug)}
             isLoading={categoriesLoading || tutorialsLoading}
           />
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col min-w-0 bg-white">
          
          {/* Minimal Content Header */}
          <div className="h-20 border-b border-slate-50 px-8 flex items-center justify-between shrink-0">
             <div className="flex items-center gap-4">
                <div className="lg:hidden">
                   <Link to="/tutoriais" className="text-black font-black uppercase tracking-tighter italic" onClick={() => closeModal()}>Lukos.</Link>
                </div>
                <div className="hidden lg:flex items-center gap-2 px-3 py-1.5 bg-slate-50 rounded-full border border-slate-100">
                   <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                   <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Live Training</span>
                </div>
             </div>

             <div className="flex items-center gap-4">
                <button 
                  onClick={() => closeModal()}
                  className="hidden lg:flex w-10 h-10 bg-slate-50 text-slate-400 hover:text-red-500 hover:bg-red-50 transition-all rounded-xl items-center justify-center border border-slate-100"
                >
                  <X size={20} />
                </button>
             </div>
          </div>

          {/* Tutorial Content: The Stage */}
          <div className="flex-1 overflow-y-auto no-scrollbar scroll-smooth">
            <TutorialViewer slug={tutorialSlug} viewMode={viewMode} focusStepId={stepId} />
          </div>
        </div>

      </motion.div>
    </div>,
    document.body
  );
};

export default TutorialModal;
